import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import {
  createPatientInsurance,
  getAgencyInsurances,
  getPatientInsurance,
  updatePatientInsurance,
} from '../../services/api';
import type {
  InsurancePosition,
  PatientInsuranceCreate,
  PatientInsuranceResponse,
  PatientResponse,
  RelationToInsured,
} from '../../services/api/types.gen';
import type { AgencyInsuranceResponse } from '../../services/api/types.gen';
import { useToast } from '../../hooks/useToastContext';
import { getUserFacingApiMessage } from '../../utils/apiErrorDetail';
import { formatDateToISO, parseISOToLocalDate } from '../../utils/dateUtils';
import { formatSSN } from '../../utils/intake';
import {
  EMPTY_PATIENT_INSURANCE_FORM,
  INSURANCE_LOCKED_MESSAGE,
  parseOptionalFloat,
  PatientInsuranceFields,
  type PatientInsuranceFormState,
} from './PatientInsuranceFields';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PatientInsuranceFormDialogProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  clinicId: string | null | undefined;
  patient?: PatientResponse | null;
  editingInsuranceId?: string | null;
}

function patientPrefillForm(
  patient: PatientResponse
): PatientInsuranceFormState {
  return {
    ...EMPTY_PATIENT_INSURANCE_FORM,
    insured_first_name: patient.first_name ?? '',
    insured_last_name: patient.last_name ?? '',
    insured_gender: patient.gender ?? '',
    insured_ssn: patient.ssn ? formatSSN(patient.ssn) : '',
    insured_dob: patient.date_of_birth
      ? (parseISOToLocalDate(patient.date_of_birth) ?? undefined)
      : undefined,
    insured_phone: patient.phone ?? '',
    insured_address: patient.address ?? '',
    insured_city: patient.city ?? '',
    insured_state: patient.state ?? '',
    insured_zip: patient.zip_code ?? '',
    relation_to_insured: 'Self',
  };
}

function insuranceToForm(
  ins: PatientInsuranceResponse
): PatientInsuranceFormState {
  return {
    agency_insurance_id: ins.agency_insurance_id,
    position: ins.position,
    insured_last_name: ins.insured_last_name ?? '',
    insured_first_name: ins.insured_first_name ?? '',
    insured_gender: ins.insured_gender ?? '',
    insured_ssn: ins.insured_ssn ? formatSSN(ins.insured_ssn) : '',
    insured_dob: ins.insured_dob
      ? (parseISOToLocalDate(ins.insured_dob) ?? undefined)
      : undefined,
    insured_phone: ins.insured_phone ?? '',
    insured_employer: ins.insured_employer ?? '',
    plan_name: ins.plan_name ?? '',
    insured_address: ins.insured_address ?? '',
    insured_city: ins.insured_city ?? '',
    insured_state: ins.insured_state ?? '',
    insured_zip: ins.insured_zip ?? '',
    relation_to_insured: ins.relation_to_insured ?? '',
    group_name: ins.group_name ?? '',
    group_number: ins.group_number ?? '',
    policy_number: ins.policy_number ?? '',
    effective_start: ins.effective_start
      ? (parseISOToLocalDate(ins.effective_start) ?? undefined)
      : undefined,
    effective_end: ins.effective_end
      ? (parseISOToLocalDate(ins.effective_end) ?? undefined)
      : undefined,
    assignment_of_benefits: ins.assignment_of_benefits ?? false,
    deductible: ins.deductible != null ? String(ins.deductible) : '',
    coverage_percent:
      ins.coverage_percent != null ? String(ins.coverage_percent) : '',
    copay_amount: ins.copay_amount != null ? String(ins.copay_amount) : '',
    insured_signature_on_file: ins.insured_signature_on_file ?? false,
  };
}

// ─── Main component ───────────────────────────────────────────────────────────

export const PatientInsuranceFormDialog: React.FC<
  PatientInsuranceFormDialogProps
> = ({ open, onClose, patientId, clinicId, patient, editingInsuranceId }) => {
  const { error: showError, success: showSuccess } = useToast();

  // ── State ─────────────────────────────────────────────────────────────────
  const [agencyInsurances, setAgencyInsurances] = useState<
    AgencyInsuranceResponse[]
  >([]);
  const [form, setForm] = useState<PatientInsuranceFormState>(
    EMPTY_PATIENT_INSURANCE_FORM
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Set<string>>(new Set());

  // ── Data loading ──────────────────────────────────────────────────────────
  const loadAgencyInsurances = useCallback(async () => {
    try {
      const res = await getAgencyInsurances({
        query: clinicId ? { agency_clinic_id: clinicId } : {},
      });
      if (res.data) setAgencyInsurances(res.data);
    } catch (err) {
      console.error('Failed to load agency insurances:', err);
    }
  }, [clinicId]);

  const loadInsuranceForEdit = useCallback(async () => {
    if (!editingInsuranceId) return;
    setIsLoading(true);
    try {
      const res = await getPatientInsurance({
        path: {
          patient_id: patientId,
          patient_insurance_id: editingInsuranceId,
        },
      });
      if (res.data) {
        setForm(insuranceToForm(res.data));
      }
    } catch (err) {
      console.error('Failed to load insurance record:', err);
      showError('Unable to load insurance details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [editingInsuranceId, patientId, showError]);

  useEffect(() => {
    if (open) {
      setErrors(new Set());
      if (editingInsuranceId) {
        void loadInsuranceForEdit();
      } else {
        setForm(
          patient ? patientPrefillForm(patient) : EMPTY_PATIENT_INSURANCE_FORM
        );
      }
      void loadAgencyInsurances();
    }
  }, [
    open,
    editingInsuranceId,
    patient,
    loadInsuranceForEdit,
    loadAgencyInsurances,
  ]);

  // ── Form helpers ──────────────────────────────────────────────────────────
  const setField = <K extends keyof PatientInsuranceFormState>(
    key: K,
    value: PatientInsuranceFormState[K]
  ) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setErrors(prev => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const newErrors = new Set<string>();
    if (!form.agency_insurance_id) newErrors.add('agency_insurance_id');
    if (!form.position) newErrors.add('position');

    if (
      form.effective_start &&
      form.effective_end &&
      form.effective_start >= form.effective_end
    ) {
      newErrors.add('effective_start');
      newErrors.add('effective_end');
    }

    if (newErrors.size > 0) {
      setErrors(newErrors);
      showError(
        newErrors.has('effective_start')
          ? 'Effective start date must be before end date.'
          : 'Please fill in all required fields.'
      );
      return;
    }

    const payload = {
      agency_insurance_id: form.agency_insurance_id,
      position: form.position as InsurancePosition,
      insured_last_name: form.insured_last_name || null,
      insured_first_name: form.insured_first_name || null,
      insured_gender:
        (form.insured_gender as PatientInsuranceCreate['insured_gender']) ||
        null,
      insured_ssn: form.insured_ssn.replace(/\D/g, '') || null,
      insured_dob: form.insured_dob ? formatDateToISO(form.insured_dob) : null,
      insured_phone: form.insured_phone || null,
      insured_employer: form.insured_employer || null,
      plan_name: form.plan_name || null,
      insured_address: form.insured_address || null,
      insured_city: form.insured_city || null,
      insured_state: form.insured_state || null,
      insured_zip: form.insured_zip || null,
      relation_to_insured:
        (form.relation_to_insured as RelationToInsured) || null,
      group_name: form.group_name || null,
      group_number: form.group_number || null,
      policy_number: form.policy_number || null,
      effective_start: form.effective_start
        ? formatDateToISO(form.effective_start)
        : null,
      effective_end: form.effective_end
        ? formatDateToISO(form.effective_end)
        : null,
      assignment_of_benefits: form.assignment_of_benefits,
      deductible: parseOptionalFloat(form.deductible),
      coverage_percent: parseOptionalFloat(form.coverage_percent),
      copay_amount: parseOptionalFloat(form.copay_amount),
      insured_signature_on_file: form.insured_signature_on_file,
    };

    setIsSaving(true);
    try {
      const response = editingInsuranceId
        ? await updatePatientInsurance({
            path: {
              patient_id: patientId,
              patient_insurance_id: editingInsuranceId,
            },
            body: payload,
          })
        : await createPatientInsurance({
            path: { patient_id: patientId },
            body: payload as PatientInsuranceCreate,
          });

      if (response.error || !response.data) {
        showError(
          getUserFacingApiMessage(
            response,
            'Unable to save insurance. Please try again.'
          )
        );
        return;
      }

      showSuccess(
        editingInsuranceId ? 'Insurance updated.' : 'Insurance added.'
      );
      onClose();
    } catch (err) {
      console.error('Failed to save insurance:', err);
      showError('Unable to save insurance. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className='sm:max-w-2xl max-h-[90vh] flex flex-col overflow-hidden'>
        <DialogHeader>
          <DialogTitle>
            {editingInsuranceId ? 'Edit Insurance' : 'Add Insurance'}
          </DialogTitle>
          <DialogDescription className='sr-only'>
            {editingInsuranceId
              ? 'Edit insurance policy'
              : 'Add new insurance policy'}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className='py-8 text-center text-sm text-muted-foreground'>
            Loading…
          </div>
        ) : (
          <div className='flex flex-col gap-4 overflow-hidden flex-1 min-h-0'>
            <div className='overflow-y-auto flex-1 min-h-0 pr-1'>
              <PatientInsuranceFields
                form={form}
                onFieldChange={setField}
                errors={errors}
                agencyInsurances={agencyInsurances}
                insuranceLockedMessage={
                  editingInsuranceId ? INSURANCE_LOCKED_MESSAGE : undefined
                }
              />
            </div>

            {/* Footer */}
            <div className='pt-3 border-t flex justify-end gap-2'>
              <Button size='sm' variant='outline' onClick={onClose}>
                Cancel
              </Button>
              <Button
                size='sm'
                onClick={() => void handleSave()}
                disabled={isSaving}
              >
                {isSaving
                  ? 'Saving…'
                  : editingInsuranceId
                    ? 'Save Changes'
                    : 'Add Insurance'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
